import { ApiClientError } from "../api/client.js";

export interface GenerationSubmissionAttemptKey {
  projectId: string;
  canvasRevision: string;
  targetNodeId: string;
  mutationVersion: number;
}

export interface GenerationSubmissionAttemptController {
  submit<Result>(
    key: GenerationSubmissionAttemptKey,
    operation: (commandId: string) => Promise<Result>
  ): Promise<Result>;
}

function sameAttempt(
  left: GenerationSubmissionAttemptKey,
  right: GenerationSubmissionAttemptKey
): boolean {
  return left.projectId === right.projectId
    && left.canvasRevision === right.canvasRevision
    && left.targetNodeId === right.targetNodeId
    && left.mutationVersion === right.mutationVersion;
}

export function createGenerationSubmissionAttemptController(options: {
  createCommandId?: () => string;
} = {}): GenerationSubmissionAttemptController {
  const createCommandId = options.createCommandId ?? (() => crypto.randomUUID());
  let current: { key: GenerationSubmissionAttemptKey; commandId: string } | undefined;

  return {
    async submit(key, operation) {
      if (!current || !sameAttempt(current.key, key)) {
        current = { key: { ...key }, commandId: createCommandId() };
      }
      const attempt = current;
      try {
        const result = await operation(attempt.commandId);
        if (current === attempt) current = undefined;
        return result;
      } catch (error) {
        // A gateway/server error can arrive after the transaction committed.
        // Keep the command id for ambiguous responses as well as network loss.
        if (error instanceof ApiClientError && error.status >= 400
          && error.status < 500 && error.status !== 408 && current === attempt) {
          current = undefined;
        }
        throw error;
      }
    }
  };
}
